<template>
  <div class="quarter-range-picker">
    <el-popover
      v-model:visible="popoverVisible"
      placement="bottom-start"
      :width="360"
      trigger="click"
    >
      <template #reference>
        <div class="picker-input" :class="{ 'is-focus': popoverVisible }">
          <input
            type="text"
            readonly
            :value="displayValue"
            :placeholder="placeholder"
            class="el-input__inner"
          />
          <el-icon class="picker-icon"><Calendar /></el-icon>
        </div>
      </template>
      
      <div class="quarter-range-panel">
        <div class="quarter-range-header">
          <el-button text @click="prevYear">
            <el-icon><ArrowLeft /></el-icon>
          </el-button>
          <span class="year-text">{{ currentYear }}年</span>
          <el-button text @click="nextYear">
            <el-icon><ArrowRight /></el-icon>
          </el-button>
        </div>
        
        <div class="quarter-range-body">
          <div class="quarter-grid">
            <div
              v-for="q in 4"
              :key="q"
              class="quarter-item"
              :class="getQuarterClass(currentYear, q)"
              @mouseenter="hoverQuarter(currentYear, q)"
              @mouseleave="hoverQuarter(null, null)"
              @click="selectQuarter(currentYear, q)"
            >
              Q{{ q }}
            </div>
          </div>
        </div>
        
        <div class="quarter-range-footer">
          <el-button size="small" @click="handleClear">清空</el-button>
          <el-button size="small" type="primary" @click="handleConfirm">确定</el-button>
        </div>
      </div>
    </el-popover>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { Calendar, ArrowLeft, ArrowRight } from '@element-plus/icons-vue'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: '选择季度范围'
  }
})

const emit = defineEmits(['update:modelValue'])

const popoverVisible = ref(false)
const currentYear = ref(new Date().getFullYear())
const tempRange = ref([])
const hoverQuarterValue = ref(null)

const displayValue = computed(() => {
  if (!props.modelValue || props.modelValue.length !== 2) return ''
  return `${props.modelValue[0]} 至 ${props.modelValue[1]}`
})

const parseQuarter = (value) => {
  const match = value.match(/(\d{4})-Q(\d)/)
  if (match) {
    return { year: parseInt(match[1]), quarter: parseInt(match[2]) }
  }
  return null
}

const quarterToNumber = (year, quarter) => {
  return year * 4 + quarter
}

const getQuarterClass = (year, quarter) => {
  const classes = []
  const qNum = quarterToNumber(year, quarter)
  
  if (tempRange.value.length === 2) {
    const startParsed = parseQuarter(tempRange.value[0])
    const endParsed = parseQuarter(tempRange.value[1])
    if (startParsed && endParsed) {
      const start = quarterToNumber(startParsed.year, startParsed.quarter)
      const end = quarterToNumber(endParsed.year, endParsed.quarter)
      const min = Math.min(start, end)
      const max = Math.max(start, end)
      
      if (qNum >= min && qNum <= max) {
        classes.push('in-range')
      }
      if (qNum === min || qNum === max) {
        classes.push('is-active')
      }
    }
  }
  
  if (hoverQuarterValue.value) {
    const hoverNum = quarterToNumber(hoverQuarterValue.value.year, hoverQuarterValue.value.quarter)
    if (tempRange.value.length === 1) {
      const startParsed = parseQuarter(tempRange.value[0])
      if (startParsed) {
        const start = quarterToNumber(startParsed.year, startParsed.quarter)
        const min = Math.min(start, hoverNum)
        const max = Math.max(start, hoverNum)
        if (qNum >= min && qNum <= max) {
          classes.push('in-range')
        }
      }
    }
  }
  
  return classes
}

const hoverQuarter = (year, quarter) => {
  if (year && quarter) {
    hoverQuarterValue.value = { year, quarter }
  } else {
    hoverQuarterValue.value = null
  }
}

const selectQuarter = (year, quarter) => {
  const value = `${year}-Q${quarter}`
  
  if (tempRange.value.length === 0 || tempRange.value.length === 2) {
    tempRange.value = [value]
  } else {
    const startParsed = parseQuarter(tempRange.value[0])
    if (startParsed) {
      const start = quarterToNumber(startParsed.year, startParsed.quarter)
      const end = quarterToNumber(year, quarter)
      
      if (start <= end) {
        tempRange.value = [tempRange.value[0], value]
      } else {
        tempRange.value = [value, tempRange.value[0]]
      }
    }
  }
}

const handleClear = () => {
  tempRange.value = []
  emit('update:modelValue', [])
  popoverVisible.value = false
}

const handleConfirm = () => {
  if (tempRange.value.length === 1) {
    tempRange.value = [tempRange.value[0], tempRange.value[0]]
  }
  emit('update:modelValue', [...tempRange.value])
  popoverVisible.value = false
}

const prevYear = () => {
  currentYear.value--
}

const nextYear = () => {
  currentYear.value++
}

watch(() => props.modelValue, (newVal) => {
  if (newVal && newVal.length === 2) {
    const start = parseQuarter(newVal[0])
    if (start) {
      currentYear.value = start.year
    }
    tempRange.value = [...newVal]
  } else {
    tempRange.value = []
  }
}, { immediate: true })

watch(() => popoverVisible.value, (visible) => {
  if (visible && props.modelValue && props.modelValue.length === 2) {
    tempRange.value = [...props.modelValue]
  } else if (visible) {
    tempRange.value = []
  }
})
</script>

<style lang="scss" scoped>
.quarter-range-picker {
  width: 100%;
}

.picker-input {
  display: flex;
  align-items: center;
  border: none;
  border-radius: 4px;
  padding: 0 12px;
  height: 32px;
  cursor: pointer;
  background: transparent;
  transition: all 0.2s;
  
  &:hover {
    border-color: transparent;
  }
  
  &.is-focus {
    border-color: transparent;
  }
  
  input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    color: #fff;
    cursor: pointer;
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.6);
    }
  }
  
  .picker-icon {
    color: #fff;
    font-size: 14px;
  }
}

.quarter-range-panel {
  .quarter-range-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    border-bottom: 1px solid #ebeef5;
    
    .year-text {
      font-size: 16px;
      font-weight: 500;
      color: #303133;
    }
  }
  
  .quarter-range-body {
    padding: 12px;
  }
  
  .quarter-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
  
  .quarter-item {
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    color: #606266;
    background: #f5f7fa;
    transition: all 0.2s;
    
    &:hover {
      color: #409eff;
      background: #ecf5ff;
    }
    
    &.is-active {
      color: #fff;
      background: #409eff;
      
      &:hover {
        background: #66b1ff;
      }
    }
    
    &.in-range {
      background: #ecf5ff;
      color: #409eff;
      border-radius: 0;
      
      &:first-child {
        border-radius: 4px 0 0 4px;
      }
      
      &:last-child {
        border-radius: 0 4px 4px 0;
      }
    }
    
    &.is-active.in-range {
      color: #fff;
      background: #409eff;
    }
  }
  
  .quarter-range-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px;
    border-top: 1px solid #ebeef5;
  }
}
</style>